(() => {
  'use strict';

  const root = document.documentElement;
  const scrolledClass = 'is-scrolled';
  const headerSelector = '[data-maison-header], .header-wrapper, #site-header';
  let animationFrame = null;

  const clamp = (value, minimum, maximum) => Math.min(Math.max(value, minimum), maximum);

  const updateScrollState = () => {
    animationFrame = null;

    const body = document.body;
    const scrollTop = window.scrollY || root.scrollTop || 0;
    const isScrolled = scrollTop > 8;
    const scrollRange = Math.max(root.scrollHeight - window.innerHeight, 0);
    const progress = scrollRange > 0 ? clamp(scrollTop / scrollRange, 0, 1) : 0;
    const progressValue = progress.toFixed(4);

    if (body) {
      body.classList.toggle(scrolledClass, isScrolled);
    }

    document.querySelectorAll(headerSelector).forEach((header) => {
      header.classList.toggle(scrolledClass, isScrolled);
    });

    root.style.setProperty('--maison-scroll-progress', progressValue);
    document.querySelectorAll('[data-maison-progress]').forEach((element) => {
      element.style.setProperty('--maison-progress', progressValue);
    });
  };

  const queueScrollUpdate = () => {
    if (animationFrame !== null) return;

    if (typeof window.requestAnimationFrame === 'function') {
      animationFrame = window.requestAnimationFrame(updateScrollState);
    } else {
      animationFrame = window.setTimeout(updateScrollState, 16);
    }
  };

  /* PDP gallery controls — Gucci-style current/total counter plus the existing
     swipe arrow. The counter is the only pagination indicator on mobile. */
  const initialiseGalleryControls = () => {
    document.querySelectorAll('.product__media-wrapper').forEach((wrapper) => {
      if (wrapper.dataset.maisonGalleryControlsWired === 'true') return;

      const rail = wrapper.querySelector('.product__media-list');
      if (!rail) return;
      wrapper.dataset.maisonGalleryControlsWired = 'true';

      const counter = document.createElement('div');
      counter.className = 'maison-gallery-counter';
      counter.setAttribute('aria-live', 'polite');
      counter.setAttribute('aria-atomic', 'true');

      const nextButton = document.createElement('button');
      nextButton.type = 'button';
      nextButton.className = 'maison-gallery-next';
      nextButton.setAttribute('aria-label', 'Next image');
      nextButton.innerHTML =
        '<svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M9 5l7 7-7 7" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      const visibleSlides = () =>
        Array.from(rail.querySelectorAll('.product__media-item')).filter((slide) => slide.clientWidth > 0);

      /* Own the overlays on the active image container, not the outer gallery.
         The media container has the actual image geometry; clipping it prevents
         either control escaping into the header if the rail briefly collapses
         while a desktop browser/theme editor switches responsive viewports. */
      const setControlOwner = (slide) => {
        const owner = slide?.querySelector('.product-media-container');
        if (!owner) return null;

        wrapper.querySelectorAll('.maison-gallery-overlay-owner').forEach((element) => {
          if (element !== owner) element.classList.remove('maison-gallery-overlay-owner');
        });
        owner.classList.add('maison-gallery-overlay-owner');
        if (counter.parentElement !== owner) owner.appendChild(counter);
        if (nextButton.parentElement !== owner) owner.appendChild(nextButton);
        return owner;
      };

      /* manual rAF glide — native smooth scroll gets cancelled by the
         mandatory snap, so snap is lifted for the 420ms of the animation.
         A tap mid-glide advances from the slide already being glided to
         (pendingIndex), so rapid taps step 1-2-3 instead of re-deriving a
         stale index from the in-flight scroll position. */
      let glideFrame = null;
      let pendingIndex = null;
      nextButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const width = rail.clientWidth;
        if (!width) return;
        const items = visibleSlides();
        if (items.length < 2) return;
        const current = pendingIndex !== null ? pendingIndex : Math.round(rail.scrollLeft / width);
        const nextIndex = current >= items.length - 1 ? 0 : current + 1;
        const target = items[nextIndex].offsetLeft - items[0].offsetLeft;
        if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          rail.scrollLeft = target;
          return;
        }
        if (glideFrame !== null) window.cancelAnimationFrame(glideFrame);
        pendingIndex = nextIndex;
        const from = rail.scrollLeft;
        const distance = target - from;
        let start = null;
        rail.style.scrollSnapType = 'none';
        const step = (timestamp) => {
          if (start === null) start = timestamp;
          const progress = Math.min((timestamp - start) / 420, 1);
          const ease = 1 - Math.pow(1 - progress, 3);
          rail.scrollLeft = from + distance * ease;
          if (progress < 1) {
            glideFrame = window.requestAnimationFrame(step);
          } else {
            glideFrame = null;
            pendingIndex = null;
            rail.style.scrollSnapType = '';
          }
        };
        glideFrame = window.requestAnimationFrame(step);
      });

      let counterFrame = null;
      let lastCounterText = '';

      const update = () => {
        counterFrame = null;
        const items = visibleSlides();
        if (!items.length) return;

        const origin = items[0].offsetLeft;
        let active = 0;
        let nearest = Infinity;
        items.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - origin - rail.scrollLeft);
          if (distance < nearest) {
            nearest = distance;
            active = index;
          }
        });
        if (!setControlOwner(items[active])) {
          counter.style.display = 'none';
          nextButton.style.display = 'none';
          return;
        }
        const nextCounterText = `${active + 1} / ${items.length}`;
        if (nextCounterText !== lastCounterText) {
          counter.textContent = nextCounterText;
          lastCounterText = nextCounterText;
        }
      };

      const queueUpdate = () => {
        if (counterFrame !== null) return;
        counterFrame = window.requestAnimationFrame ? window.requestAnimationFrame(update) : window.setTimeout(update, 16);
      };

      const rebuild = () => {
        const items = visibleSlides();
        if (items.length < 2) {
          counter.style.display = 'none';
          nextButton.style.display = 'none';
          return;
        }
        counter.style.display = '';
        nextButton.style.display = '';
        update();
      };

      rail.addEventListener('scroll', queueUpdate, { passive: true });
      window.addEventListener('resize', rebuild, { passive: true });
      rebuild();
    });
  };

  /* Product-gallery films are intentionally not autoplayed from initial HTML.
     Start them only when they approach the real viewport; offscreen desktop
     media and unswiped mobile slides therefore do not compete with the hero. */
  let productVideoObserver = null;
  const initialiseDeferredProductVideos = (scope = document) => {
    const videos = scope.querySelectorAll
      ? scope.querySelectorAll('video.maison-product-video--deferred')
      : [];
    if (!videos.length) return;

    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (!reducedMotion && 'IntersectionObserver' in window && !productVideoObserver) {
      productVideoObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const video = entry.target;
          if (entry.isIntersecting) {
            video.preload = 'metadata';
            const playback = video.play();
            if (playback && typeof playback.catch === 'function') {
              playback.catch(() => { video.controls = true; });
            }
          } else {
            video.pause();
          }
        });
      }, { rootMargin: '240px 0px', threshold: 0.01 });
    }

    videos.forEach((video) => {
      if (video.dataset.maisonDeferredVideoWired === 'true') return;
      video.dataset.maisonDeferredVideoWired = 'true';
      video.autoplay = false;
      video.preload = 'none';
      video.muted = true;
      video.playsInline = true;

      if (reducedMotion || !productVideoObserver) {
        video.controls = true;
        return;
      }
      productVideoObserver.observe(video);
    });
  };

  /* SIZE GUIDE — reveal the size-row trigger when the Best Fit size-chart app
     is on the page, and proxy clicks to the app's own (hidden) opener.
     The app injects late, so retry briefly before giving up. */
  let sizeGuideAttempts = 0;
  const initialiseSizeGuide = () => {
    const pending = Array.from(document.querySelectorAll('[data-maison-size-guide]')).filter(
      (trigger) => !trigger.dataset.maisonSizeGuideWired
    );
    if (!pending.length) return;

    const appTrigger = document.querySelector('.scr-open-size-chart');
    if (!appTrigger) {
      if (sizeGuideAttempts < 20) {
        sizeGuideAttempts += 1;
        window.setTimeout(initialiseSizeGuide, 500);
      }
      return;
    }

    pending.forEach((trigger) => {
      trigger.dataset.maisonSizeGuideWired = 'true';
      trigger.hidden = false;
      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        appTrigger.click();
      });
    });
  };

  /* Reviews section — hide the chrome while the Okendo widget is empty
     (zero reviews / metafields not yet synced), reveal when it populates */
  const initialiseReviewsSection = () => {
    document.querySelectorAll('.maison-reviews').forEach((section) => {
      const widget = section.querySelector('[data-oke-widget]');
      const host = section.closest('.shopify-section') || section;
      if (!widget) {
        host.style.display = 'none';
        return;
      }
      const sync = () => {
        host.style.display = widget.childElementCount > 0 ? '' : 'none';
      };
      sync();
      new MutationObserver(sync).observe(widget, { childList: true, subtree: true });
    });
  };

  /* Recently viewed — localStorage history (maison:recently-viewed:v1),
     rendered client-side from /products/{handle}.js; section hides when empty */
  const RV_KEY = 'maison:recently-viewed:v1';

  const readRecentlyViewed = () => {
    try {
      const parsed = JSON.parse(window.localStorage.getItem(RV_KEY));
      if (!Array.isArray(parsed)) return [];
      /* entries are {handle, sw?} objects; legacy entries were bare strings */
      return parsed
        .map((entry) => (typeof entry === 'string' ? { handle: entry } : entry))
        .filter((entry) => entry && typeof entry.handle === 'string');
    } catch (error) {
      return [];
    }
  };

  const trackRecentlyViewed = () => {
    const section = document.querySelector('[data-maison-rv-current]');
    if (!section || !section.dataset.maisonRvCurrent) return;
    const handle = section.dataset.maisonRvCurrent;
    let swatches = [];
    try {
      swatches = JSON.parse(section.dataset.maisonRvSwatches || '[]').slice(0, 4);
    } catch (error) { /* malformed attribute */ }
    try {
      const entry = { handle };
      if (swatches.length) entry.sw = swatches;
      const next = [entry, ...readRecentlyViewed().filter((e) => e.handle !== handle)].slice(0, 12);
      window.localStorage.setItem(RV_KEY, JSON.stringify(next));
    } catch (error) { /* private mode */ }
  };

  const formatMoney = (cents) => {
    const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    const amount = cents / 100;
    const formatted = new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    return formatted.replace(/\.00(?=\D*$)/, '');
  };

  const escapeAttr = (value) => String(value).replace(/[&<>"']/g, (ch) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
  ));

  const renderRecentlyViewed = () => {
    const section = document.querySelector('[data-maison-rv]');
    if (!section) return;
    const host = section.closest('.shopify-section') || section;
    const current = section.dataset.maisonRvCurrent;
    const entries = readRecentlyViewed().filter((e) => e.handle !== current).slice(0, 8);
    const swatchesByHandle = {};
    entries.forEach((e) => { if (Array.isArray(e.sw) && e.sw.length) swatchesByHandle[e.handle] = e.sw; });
    const handles = entries.map((e) => e.handle);
    if (!handles.length) {
      host.style.display = 'none';
      return;
    }

    Promise.all(
      handles.map((handle) =>
        fetch('/products/' + handle + '.js')
          .then((response) => (response.ok ? response.json() : null))
          .catch(() => null)
      )
    ).then((results) => {
      const products = results.filter(Boolean);
      if (!products.length) {
        host.style.display = 'none';
        return;
      }
      const rail = section.querySelector('[data-maison-rv-rail]');
      rail.innerHTML = products
        .map((product) => {
          const joiner = product.featured_image && product.featured_image.indexOf('?') >= 0 ? '&' : '?';
          const image = product.featured_image
            ? '<img src="' + product.featured_image + joiner + 'width=600" alt="" loading="lazy">'
            : '<span class="maison-rv__noimg" aria-hidden="true"></span>';
          const compare = product.compare_at_price && product.compare_at_price > product.price
            ? '<s>' + formatMoney(product.compare_at_price) + '</s> '
            : '';
          const priceClass = compare ? ' maison-rv__price--sale' : '';
          /* swatches ride in from the stored entry (Liquid-captured on the
             visited PDP) — the .js endpoint exposes no metafields */
          const swatchSpans = (swatchesByHandle[product.handle] || [])
            .map((s) => (s && s.color
              ? '<span title="' + escapeAttr(s.label || '') + '" style="--maison-swatch: ' + escapeAttr(s.color) + ';"></span>'
              : ''))
            .join('');
          const swatches = swatchSpans
            ? '<span class="maison-product-card__swatches maison-rv__swatches" aria-label="Colours">' + swatchSpans + '</span>'
            : '';
          return (
            '<a class="maison-rv__card" href="' + product.url + '">' +
            '<span class="maison-rv__media">' + image + '</span>' +
            '<span class="maison-rv__title">' + product.title + '</span>' +
            '<span class="maison-rv__price' + priceClass + '">' + compare + formatMoney(product.price) + '</span>' +
            swatches +
            '</a>'
          );
        })
        .join('');
      host.style.display = '';
    });
  };

  /* mobile next-button for recommendation rails (design: 38px glass circle).
     Buttons are rendered in Liquid (they survive product-recommendations
     refetches); a single delegated listener drives them all. */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.maison-rec-next');
    if (!button) return;
    const wrap = button.closest('[data-maison-rec-rail]');
    const track = wrap && (wrap.querySelector('[data-maison-rec-track]') || wrap.querySelector('ul'));
    if (!track) return;
    const slide = track.querySelector(':scope > *');
    const step = slide ? slide.getBoundingClientRect().width + 12 : track.clientWidth * 0.68;
    track.scrollBy({ left: step, behavior: 'smooth' });
  });

  /* Product-card gallery. The chevron advances with wrap-around, while mobile
     collection cards also accept a horizontal swipe. Delegation survives
     facets and infinite-scroll replacements. */
  const cardMediaUrls = (media) => {
    try { return JSON.parse(media.getAttribute('data-card-media') || '[]'); }
    catch (error) { return []; }
  };

  const updateCardCounter = (media, index, total) => {
    const counter = media && media.querySelector('.maison-product-card__counter');
    if (counter && total > 1) counter.textContent = `${index + 1} / ${total}`;
  };

  const advanceCardMedia = (media, direction = 1) => {
    if (!media) return false;
    const urls = cardMediaUrls(media);
    if (urls.length < 2) return false;
    const image = media.querySelector('img:not(.maison-product-card__hover-img)');
    if (!image) return false;
    let index = Number(media.dataset.maisonMediaIndex || 0);
    /* A fine-pointer hover preview may already be painting image 2 over an
       index-0 card. A forward chevron click should advance from what is visible. */
    if (direction > 0 && index === 0) {
      const hoverImg = media.querySelector('.maison-product-card__hover-img');
      if (hoverImg && parseFloat(getComputedStyle(hoverImg).opacity) > 0.5) index = 1;
    }
    const next = (index + direction + urls.length) % urls.length;
    media.dataset.maisonMediaIndex = String(next);
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = urls[next];
    const preload = new Image();
    preload.src = urls[(next + (direction < 0 ? -1 : 1) + urls.length) % urls.length];
    updateCardCounter(media, next, urls.length);
    const video = media.querySelector('.maison-product-card__hover-video');
    if (video && video.src) {
      if (next === 0 && media.matches(':hover')) playCardVideo(video);
      else video.pause();
    }
    return true;
  };

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('.maison-product-card__next');
    if (!arrow) return;
    event.preventDefault();
    event.stopPropagation();
    advanceCardMedia(arrow.closest('.maison-product-card__media'), 1);
  });

  /* The media remains a real product link: a tap still navigates. Only a clear
     horizontal gesture is claimed by the gallery, and its synthetic follow-up
     click is suppressed so the swipe cannot open the product accidentally. */
  let cardSwipe = null;
  const isCollectionCardMedia = (target) => {
    if (!document.body.classList.contains('template-collection')) return null;
    if (!window.matchMedia('(max-width: 989px)').matches) return null;
    return target && target.closest ? target.closest('.maison-product-card__media[data-card-media]') : null;
  };

  document.addEventListener('pointerdown', (event) => {
    if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
    if (!event.isPrimary) return;
    const media = isCollectionCardMedia(event.target);
    if (!media || cardMediaUrls(media).length < 2) return;
    cardSwipe = {
      pointerId: event.pointerId,
      media,
      startX: event.clientX,
      startY: event.clientY,
    };
  }, { passive: true });

  document.addEventListener('pointerup', (event) => {
    if (!cardSwipe || event.pointerId !== cardSwipe.pointerId) return;
    const swipe = cardSwipe;
    cardSwipe = null;
    const deltaX = event.clientX - swipe.startX;
    const deltaY = event.clientY - swipe.startY;
    const horizontal = Math.abs(deltaX) >= 32 && Math.abs(deltaX) > Math.abs(deltaY) * 1.15;
    if (!horizontal) return;
    event.preventDefault();
    event.stopPropagation();
    swipe.media.dataset.maisonSuppressClickUntil = String(Date.now() + 700);
    advanceCardMedia(swipe.media, deltaX < 0 ? 1 : -1);
  });

  document.addEventListener('pointercancel', (event) => {
    if (cardSwipe && event.pointerId === cardSwipe.pointerId) cardSwipe = null;
  }, { passive: true });

  document.addEventListener('click', (event) => {
    const media = isCollectionCardMedia(event.target);
    if (!media) return;
    const suppressUntil = Number(media.dataset.maisonSuppressClickUntil || 0);
    if (suppressUntil <= Date.now()) {
      delete media.dataset.maisonSuppressClickUntil;
      return;
    }
    delete media.dataset.maisonSuppressClickUntil;
    event.preventDefault();
    event.stopPropagation();
  }, true);

  /* PLP card hover video: preload=none and no src until the first hover, so
     the page never pays for video bytes up front. The image-2 preview under
     it covers the first-hover buffering; .is-playing gates the fade so a
     stalled or autoplay-blocked video simply leaves the image showing. */
  const playCardVideo = (video) => {
    if (!video.src) {
      /* Liquid only exposes the HD master mp4; Shopify hosts an SD-480p
         rendition at a sibling URL. A PLP tile never needs 1080p and 1.5Mbps
         starts faster, so try SD first and fall back to the master onerror. */
      const master = video.dataset.cardVideo;
      const sd = master.replace(/HD-(?:1080|720)p-[\d.]+Mbps/, 'SD-480p-1.5Mbps');
      if (sd !== master) {
        video.addEventListener('error', () => {
          video.src = master;
          const retry = video.play();
          if (retry) retry.then(() => video.classList.add('is-playing')).catch(() => {});
        }, { once: true });
        /* Low-res uploads have SD siblings as narrow as 270px, too soft for a
           ~340px tile — once metadata reveals that, step up to the 720p
           sibling (the error listener above still guards a missing file). */
        video.addEventListener('loadedmetadata', () => {
          if (!video.videoWidth || video.videoWidth >= 380) return;
          if (!/SD-480p/.test(video.src)) return;
          video.src = master.replace(/HD-1080p-[\d.]+Mbps/, 'HD-720p-4.5Mbps');
          const card = video.closest('.maison-product-card');
          if (card && card.matches(':hover')) {
            const up = video.play();
            if (up) up.then(() => video.classList.add('is-playing')).catch(() => {});
          }
        }, { once: true });
      }
      video.src = sd;
    }
    video.muted = true;
    const started = video.play();
    if (started) started.then(() => video.classList.add('is-playing')).catch(() => {});
  };

  document.addEventListener('mouseover', (event) => {
    const card = event.target.closest('.maison-product-card');
    if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
    const media = card.querySelector('.maison-product-card__media');
    const video = card.querySelector('.maison-product-card__hover-video');
    if (!media) return;
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    if (Number(media.dataset.maisonMediaIndex || 0) !== 0) return;
    const urls = cardMediaUrls(media);
    if (media.querySelector('.maison-product-card__hover-img')) updateCardCounter(media, 1, urls.length);
    if (!video) return;
    playCardVideo(video);
  });

  document.addEventListener('mouseout', (event) => {
    const card = event.target.closest('.maison-product-card');
    if (!card || (event.relatedTarget && card.contains(event.relatedTarget))) return;
    const media = card.querySelector('.maison-product-card__media');
    if (media && Number(media.dataset.maisonMediaIndex || 0) === 0) {
      updateCardCounter(media, 0, cardMediaUrls(media).length);
    }
    const video = card.querySelector('.maison-product-card__hover-video');
    if (video && video.src) video.pause();
  });

  /* PLP endless scroll (replaces the VIEW ALL row): a sentinel under the grid
     prefetches the next page ~1200px early via the Section Rendering API and
     appends the new cards. Per-page white grid fillers are stripped before
     each append and the incoming page brings its own (per-page counts stay
     congruent with the cumulative count while products_per_page is a multiple
     of both column counts). Scroll-reveal classes are stripped from incoming
     items — they arrive mid-scroll and must not wait on the animation
     observer. Facets re-renders replace the sentinel with one carrying the
     filtered next URL; the MutationObserver re-arms it. */
  const loadMoreProducts = (sentinel) => {
    if (sentinel.dataset.maisonLoading) return;
    sentinel.dataset.maisonLoading = 'true';
    const url = new URL(sentinel.dataset.nextUrl, window.location.origin);
    url.searchParams.set('section_id', sentinel.dataset.sectionId);
    fetch(url.toString())
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((html) => {
        /* A facets re-render replaces the grid AND this sentinel mid-flight;
           appending then would push the stale page into the new grid. The
           fresh sentinel owns the fresh state — a detached one must bail. */
        if (!sentinel.isConnected) return;
        const fetched = new DOMParser().parseFromString(html, 'text/html');
        const grid = document.getElementById('product-grid');
        const incoming = fetched.getElementById('product-grid');
        if (!grid || !incoming) throw new Error('grid missing');
        grid.querySelectorAll('.maison-plp-fill').forEach((fill) => fill.remove());
        const fragment = document.createDocumentFragment();
        [...incoming.children].forEach((item) => {
          item.classList.remove('scroll-trigger', 'animate--slide-in');
          item.removeAttribute('data-cascade');
          fragment.appendChild(item);
        });
        grid.appendChild(fragment);
        document.dispatchEvent(new CustomEvent('maison:grid:append'));
        const next = fetched.querySelector('[data-maison-infinite]');
        const nextUrl = next && next.getAttribute('data-next-url');
        if (nextUrl) {
          sentinel.dataset.nextUrl = nextUrl;
          delete sentinel.dataset.maisonLoading;
          infiniteIO.unobserve(sentinel);
          infiniteIO.observe(sentinel);
        } else {
          infiniteIO.unobserve(sentinel);
          sentinel.remove();
        }
      })
      .catch(() => {
        delete sentinel.dataset.maisonLoading;
        if (!sentinel.isConnected) return;
        const retries = Number(sentinel.dataset.maisonRetries || 0) + 1;
        sentinel.dataset.maisonRetries = String(retries);
        infiniteIO.unobserve(sentinel);
        if (retries >= 3) return;
        sentinel.setAttribute('data-maison-backoff', '');
        setTimeout(() => {
          sentinel.removeAttribute('data-maison-backoff');
          infiniteIO.observe(sentinel);
        }, 3000 * retries);
      });
  };

  const infiniteIO = new IntersectionObserver((entries) => {
    entries.forEach((entry) => { if (entry.isIntersecting) loadMoreProducts(entry.target); });
  }, { rootMargin: '1200px 0px' });

  const armInfiniteScroll = () => {
    document.querySelectorAll('[data-maison-infinite][data-next-url]:not([data-maison-backoff])').forEach((el) => infiniteIO.observe(el));
  };
  armInfiniteScroll();
  const gridContainer = document.getElementById('ProductGridContainer');
  if (gridContainer) new MutationObserver(armInfiniteScroll).observe(gridContainer, { childList: true, subtree: true });

  /* Desktop gallery hover-zoom (design v4): ink-ring cursor badge with a thin
     +/−, click = in-place scale(2.2) zoom whose origin pans with the cursor.
     Mouse clicks replace the lightbox on fine-pointer desktops; keyboard
     activation (detail 0) still reaches the modal for accessibility. */
  const ZOOM_PLUS = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M8 1.5v13M1.5 8h13" stroke="#16140f" stroke-width="1.1"/></svg>';
  const ZOOM_MINUS = '<svg width="15" height="15" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M1.5 8h13" stroke="#16140f" stroke-width="1.1"/></svg>';

  const initialiseGalleryZoom = () => {
    if (!document.body.classList.contains('template-product')) return;
    if (!window.matchMedia('(min-width: 990px) and (pointer: fine)').matches) return;

    document.querySelectorAll('.product__media-item .product-media-container').forEach((tile) => {
      if (tile.dataset.maisonZoomWired) return;
      if (/media-type-(video|external_video|model)/.test(tile.className)) return;
      const media = tile.querySelector('.product__media img');
      if (!media || tile.querySelector('video, deferred-media')) return;
      tile.dataset.maisonZoomWired = 'true';
      tile.classList.add('maison-zoom-tile');

      const badge = document.createElement('div');
      badge.className = 'maison-zoom-badge';
      badge.setAttribute('aria-hidden', 'true');
      badge.innerHTML = ZOOM_PLUS;
      tile.appendChild(badge);

      let zoomed = false;
      const point = (event) => {
        const rect = tile.getBoundingClientRect();
        return {
          rect,
          x: Math.max(0, Math.min(100, ((event.clientX - rect.left) / rect.width) * 100)),
          y: Math.max(0, Math.min(100, ((event.clientY - rect.top) / rect.height) * 100)),
        };
      };

      tile.addEventListener('mousemove', (event) => {
        const at = point(event);
        badge.style.display = 'flex';
        badge.style.transform = 'translate(' + (event.clientX - at.rect.left - 21) + 'px,' + (event.clientY - at.rect.top - 21) + 'px)';
        if (zoomed) media.style.transformOrigin = at.x + '% ' + at.y + '%';
      });
      tile.addEventListener('mouseleave', () => {
        badge.style.display = 'none';
      });
      tile.addEventListener(
        'click',
        (event) => {
          if (event.detail === 0) return; // keyboard: let the lightbox open
          event.preventDefault();
          event.stopPropagation();
          zoomed = !zoomed;
          const at = point(event);
          media.style.transformOrigin = at.x + '% ' + at.y + '%';
          media.style.transform = zoomed ? 'scale(2.2)' : 'scale(1)';
          badge.innerHTML = zoomed ? ZOOM_MINUS : ZOOM_PLUS;
        },
        true
      );
    });
  };

  /* Homepage collection rail — a single, quiet discovery glide. It reveals a
     little more of the next card, returns to the first card, and never loops.
     Any shopper interaction wins immediately; reduced-motion users get none. */
  const initialiseHomeProductRailDiscovery = (scope = document) => {
    const reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const fineHover = window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

    scope.querySelectorAll('[data-maison-home] [data-maison-product-index]').forEach((rail) => {
      if (rail.dataset.maisonDiscoveryGlide) return;
      rail.dataset.maisonDiscoveryGlide = 'armed';

      if (reducedMotion) {
        rail.dataset.maisonDiscoveryGlide = 'reduced-motion';
        return;
      }

      let observer = null;
      let delayTimer = null;
      let animationFrame = null;
      let running = false;
      let settled = false;
      let origin = 0;
      const originalSnapType = rail.style.scrollSnapType;
      const originalScrollBehavior = rail.style.scrollBehavior;
      const interactionEvents = ['pointerdown', 'touchstart', 'wheel', 'keydown'];

      const easeInOutCubic = (value) =>
        value < 0.5 ? 4 * value * value * value : 1 - Math.pow(-2 * value + 2, 3) / 2;

      const restoreOrigin = () => {
        rail.scrollLeft = origin;
        rail.style.scrollSnapType = originalSnapType;
        rail.style.scrollBehavior = originalScrollBehavior;
      };

      const removeListeners = () => {
        interactionEvents.forEach((eventName) => rail.removeEventListener(eventName, cancelFromInteraction));
        rail.removeEventListener('pointerenter', cancelFromHover);
        document.removeEventListener('visibilitychange', cancelWhenHidden);
        window.removeEventListener('resize', cancelFromResize);
      };

      const settle = (state) => {
        if (settled) return;
        const shouldReturnToOrigin = running;
        settled = true;
        if (delayTimer !== null) window.clearTimeout(delayTimer);
        if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
        if (observer) observer.disconnect();
        if (shouldReturnToOrigin) restoreOrigin();
        running = false;
        rail.dataset.maisonDiscoveryGlide = state;
        removeListeners();
      };

      function cancelFromInteraction() {
        settle('cancelled');
      }

      function cancelFromHover() {
        if (fineHover && running) settle('cancelled');
      }

      function cancelWhenHidden() {
        if (document.hidden) settle('cancelled');
      }

      function cancelFromResize() {
        if (running) settle('cancelled');
      }

      const run = () => {
        delayTimer = null;
        if (settled) return;
        if (!rail.isConnected) {
          settle('detached');
          return;
        }
        if (Math.abs(rail.scrollLeft) > 1 || rail.contains(document.activeElement)) {
          settle('skipped');
          return;
        }

        const available = rail.scrollWidth - rail.clientWidth;
        if (available < 8) {
          settle('no-overflow');
          return;
        }

        origin = rail.scrollLeft;
        const mobile = window.matchMedia && window.matchMedia('(max-width: 749px)').matches;
        const gutter = parseFloat(window.getComputedStyle(rail).paddingLeft);
        const distance = Math.min(Number.isFinite(gutter) && gutter > 0 ? gutter : (mobile ? 22 : 40), 40, available);
        const outwardDuration = mobile ? 1050 : 1200;
        const holdDuration = 180;
        const returnDuration = mobile ? 900 : 1000;
        const totalDuration = outwardDuration + holdDuration + returnDuration;
        let startedAt = null;

        running = true;
        rail.dataset.maisonDiscoveryGlide = 'running';
        rail.style.scrollSnapType = 'none';
        rail.style.scrollBehavior = 'auto';

        const step = (timestamp) => {
          if (settled) return;
          if (startedAt === null) startedAt = timestamp;
          const elapsed = timestamp - startedAt;
          let offset;

          if (elapsed < outwardDuration) {
            offset = distance * easeInOutCubic(elapsed / outwardDuration);
          } else if (elapsed < outwardDuration + holdDuration) {
            offset = distance;
          } else {
            const returnProgress = Math.min((elapsed - outwardDuration - holdDuration) / returnDuration, 1);
            offset = distance * (1 - easeInOutCubic(returnProgress));
          }

          rail.scrollLeft = origin + offset;
          if (elapsed < totalDuration) {
            animationFrame = window.requestAnimationFrame(step);
          } else {
            animationFrame = null;
            settle('complete');
          }
        };

        animationFrame = window.requestAnimationFrame(step);
      };

      const schedule = () => {
        if (settled || delayTimer !== null) return;
        if (observer) observer.disconnect();
        delayTimer = window.setTimeout(run, 700);
      };

      interactionEvents.forEach((eventName) => rail.addEventListener(eventName, cancelFromInteraction, { passive: true }));
      rail.addEventListener('pointerenter', cancelFromHover, { passive: true });
      document.addEventListener('visibilitychange', cancelWhenHidden);
      window.addEventListener('resize', cancelFromResize, { passive: true });

      if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(
          (entries) => {
            if (entries.some((entry) => entry.isIntersecting && entry.intersectionRatio >= 0.2)) schedule();
          },
          { threshold: [0.2], rootMargin: '0px 0px -10% 0px' }
        );
        observer.observe(rail);
      } else {
        schedule();
      }
    });
  };

  const initialiseMaison = () => {
    if (!document.body) return;

    root.classList.add('maison-js');
    document.body.classList.add('is-maison-ready');
    updateScrollState();
    initialiseGalleryControls();
    initialiseDeferredProductVideos();
    initialiseSizeGuide();
    initialiseReviewsSection();
    renderRecentlyViewed();
    trackRecentlyViewed();
    initialiseGalleryZoom();
    initialiseHomeProductRailDiscovery();
    document.dispatchEvent(new CustomEvent('maison:ready'));
  };

  window.addEventListener('scroll', queueScrollUpdate, { passive: true });
  window.addEventListener('resize', queueScrollUpdate, { passive: true });
  document.addEventListener('shopify:section:load', (event) => {
    queueScrollUpdate();
    initialiseGalleryControls();
    initialiseDeferredProductVideos(event.target || document);
    initialiseSizeGuide();
    initialiseReviewsSection();
    renderRecentlyViewed();
    initialiseGalleryZoom();
    initialiseHomeProductRailDiscovery(event.target || document);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialiseMaison, { once: true });
  } else {
    initialiseMaison();
  }
})();

/* Maison: preorder (Stoq selling-plan) pricing - while a DISCOUNTED plan radio is
   selected, show the prerendered plan price set (struck base + plan price, sale
   styling) + the PREORDER chip, and mirror the price inside the ATC button.
   Prices are Liquid-prerendered per variant x plan (Markets-correct); this only
   toggles visibility and copies strings. Idempotent via a state key because the
   MutationObserver watches the form the app re-renders into. */
(function () {
  if (window.maisonPreorderPricing) return;
  window.maisonPreorderPricing = true;
  var lastKey = null;
  function apply() {
    var planSpans = document.querySelectorAll('[data-maison-plan-price]');
    if (!planSpans.length) return;
    var idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    var vid = idInput ? String(idInput.value) : null;
    var checked = document.querySelector('.restock-rocket-payment-input:checked');
    var planId = checked ? String(checked.value) : null;
    var active = null;
    planSpans.forEach(function (s) {
      if (vid && planId && s.dataset.variantId === vid && s.dataset.planId === planId) active = s;
    });
    var key = active ? vid + ':' + planId : 'base';
    if (key === lastKey) return;
    lastKey = key;
    planSpans.forEach(function (s) { s.hidden = s !== active; });
    var base = document.querySelector('[data-maison-price-base]');
    if (base) base.hidden = !!active;
    var chip = document.querySelector('[data-maison-preorder-chip]');
    if (chip) {
      chip.hidden = !active;
      if (active) chip.textContent = 'PREORDER · SAVE ' + active.dataset.save + '%';
    }
    var atc = document.querySelector('.product-form__buttons .maison-atc-price');
    if (atc) {
      if (!atc.dataset.maisonBaseHtml) atc.dataset.maisonBaseHtml = atc.innerHTML;
      if (active) {
        var compare = active.querySelector('.maison-pdp-price-compare');
        var price = active.querySelector('.maison-pdp-price');
        atc.innerHTML = '<s class="maison-atc-price__compare">' + compare.textContent.trim() + '</s> ' + price.textContent.trim();
      } else {
        atc.innerHTML = atc.dataset.maisonBaseHtml;
      }
    }
  }
  function schedule() { window.setTimeout(apply, 60); }
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.matches) return;
    if (t.matches('.restock-rocket-payment-input') || t.matches('variant-radios input[type="radio"]')) schedule();
  });
  var boot = function () {
    var form = document.querySelector('form[action*="/cart/add"]');
    if (form) new MutationObserver(schedule).observe(form, { childList: true, subtree: true });
    schedule();
    window.setTimeout(apply, 800);
    window.setTimeout(apply, 2500); /* app checks the default plan radio late */
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();

/* Maison: search curtain — (1) the sticky header is a transformed ancestor, so the
   fixed search modal resolves against it; measure the real header bottom into
   --maison-search-top on open/resize. (2) Dawn's DetailsModal removes [open]
   instantly on close; hold it 800ms behind a .maison-search-closing class so the
   .75s curtain retract can play (same trick as the menu drawer close patch). */
(function () {
  if (window.maisonSearchCurtain) return;
  window.maisonSearchCurtain = true;
  function setTop() {
    var header = document.querySelector('.section-header') || document.querySelector('header.header');
    if (!header) return;
    var b = header.getBoundingClientRect().bottom;
    if (b > 0 && b < window.innerHeight) document.documentElement.style.setProperty('--maison-search-top', (Math.round(b * 10) / 10 + 1) + 'px');
  }
  document.addEventListener('click', function (e) {
    if (e.target && e.target.closest && e.target.closest('.header__search summary')) setTop();
  }, true);
  window.addEventListener('resize', setTop);
  window.customElements.whenDefined('details-modal').then(function () {
    var proto = window.customElements.get('details-modal').prototype;
    if (proto.__maisonSearchClose) return;
    proto.__maisonSearchClose = true;
    var origClose = proto.close;
    proto.close = function (focusToggle) {
      var self = this;
      var details = this.querySelector('details');
      if (!this.closest || !this.closest('.header__search') || this.__maisonClosing || !details || !details.hasAttribute('open') || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        return origClose.call(this, focusToggle);
      }
      this.__maisonClosing = true;
      details.classList.add('maison-search-closing');
      window.setTimeout(function () {
        details.classList.remove('maison-search-closing');
        self.__maisonClosing = false;
        origClose.call(self, focusToggle);
      }, 800);
    };
  });
})();

/* Maison PLP density controls: preserves the guttered sparse-grid contract while
   adding the Open Design 1/2-column mobile and refined 3/4-column desktop selectors.
   Cards rehang with a measured FLIP glide; a MutationObserver reapplies the
   chosen view after facets.js swaps the grid or infinite scroll appends cards. */
(function () {
  if (window.maisonPlpDensity) return;
  window.maisonPlpDensity = true;

  var desktopMQ = window.matchMedia('(min-width: 990px)');
  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  var MOBILE_KEY = 'maison:plp-view:mobile';
  var DESKTOP_KEY = 'maison:plp-view:desktop';
  var viewTransitioning = false;
  var applyFrame = null;

  function isDesktop() { return desktopMQ.matches; }
  function normalise(view) {
    if (isDesktop()) return view === 'three' ? 'three' : 'four';
    return view === 'one' ? 'one' : 'two';
  }
  function savedView() {
    var fallback = isDesktop() ? 'four' : 'two';
    try { return normalise(window.localStorage.getItem(isDesktop() ? DESKTOP_KEY : MOBILE_KEY) || fallback); }
    catch (_) { return fallback; }
  }
  function saveView(view) {
    try { window.localStorage.setItem(isDesktop() ? DESKTOP_KEY : MOBILE_KEY, view); }
    catch (_) { /* private mode */ }
  }
  function cards(grid) {
    return Array.from(grid.querySelectorAll(':scope > li.grid__item:not(.maison-plp-fill)'));
  }
  function updateImageSizes(grid, view) {
    var size = isDesktop() ? (view === 'three' ? '33.333vw' : '25vw') : (view === 'one' ? '100vw' : '50vw');
    grid.querySelectorAll('.maison-product-card__media img').forEach(function (image) {
      image.setAttribute('sizes', size);
    });
  }
  function updateButtons(view) {
    document.querySelectorAll('.maison-plp-view-button[data-maison-plp-view]').forEach(function (button) {
      var active = button.dataset.maisonPlpView === view;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-pressed', String(active));
    });
  }
  function apply(view, persist) {
    var grid = document.getElementById('product-grid');
    if (!grid) return null;
    var next = normalise(view);
    grid.classList.remove('maison-plp-view--one', 'maison-plp-view--two', 'maison-plp-view--three', 'maison-plp-view--four');
    grid.classList.add('maison-plp-view--' + next);
    grid.dataset.maisonPlpView = next;
    var count = cards(grid).length;
    var preferredColumns = next === 'one' ? 1 : (next === 'two' ? 2 : (next === 'three' ? 3 : 4));
    if (count) grid.style.setProperty('--maison-grid-cols', Math.min(preferredColumns, count));
    else grid.style.removeProperty('--maison-grid-cols');
    updateImageSizes(grid, next);
    updateButtons(next);
    if (persist) saveView(next);
    return next;
  }
  function bloom(button) {
    button.classList.remove('is-blooming');
    void button.offsetWidth;
    button.classList.add('is-blooming');
    window.setTimeout(function () { button.classList.remove('is-blooming'); }, 680);
  }
  function animate(view, button) {
    var grid = document.getElementById('product-grid');
    if (!grid) return;
    var next = normalise(view);
    bloom(button);
    if (grid.dataset.maisonPlpView === next || viewTransitioning) return;
    if (reduceMQ.matches || !Element.prototype.animate) {
      apply(next, true);
      return;
    }

    viewTransitioning = true;
    var movingCards = cards(grid);
    var first = movingCards.map(function (card) { return card.getBoundingClientRect(); });
    apply(next, true);
    grid.classList.add('is-reflowing');

    window.requestAnimationFrame(function () {
      var desktop = isDesktop();
      var promises = movingCards.map(function (card, index) {
        if (!card.isConnected) return Promise.resolve();
        var last = card.getBoundingClientRect();
        var start = first[index];
        if (!last.width || !last.height) return Promise.resolve();
        var dx = start.left - last.left;
        var dy = start.top - last.top;
        var sx = start.width / last.width;
        var sy = start.height / last.height;
        var delay = Math.min(index, desktop ? 7 : 5) * (desktop ? 34 : 38);
        var move = card.animate([
          { transform: 'translate(' + dx + 'px,' + dy + 'px) scale(' + sx + ',' + sy + ')', opacity: desktop ? .82 : .84 },
          { transform: 'translate(0,0) scale(1,1)', opacity: 1 }
        ], {
          duration: desktop ? 820 : 760,
          delay: delay,
          easing: 'cubic-bezier(.16,1,.3,1)',
          fill: 'both'
        });
        var media = card.querySelector('.maison-product-card__media');
        if (media) {
          media.animate([
            { clipPath: 'inset(0 0 0 0)' },
            { clipPath: 'inset(' + (desktop ? '2.5%' : '2.2%') + ' 0)' },
            { clipPath: 'inset(0 0 0 0)' }
          ], {
            duration: desktop ? 620 : 580,
            delay: delay + (desktop ? 45 : 40),
            easing: 'cubic-bezier(.16,1,.3,1)'
          });
        }
        return move.finished.catch(function () {});
      });
      Promise.all(promises).then(function () {
        grid.classList.remove('is-reflowing');
        viewTransitioning = false;
      });
    });
  }
  function queueApply() {
    if (applyFrame !== null) return;
    applyFrame = window.requestAnimationFrame(function () {
      applyFrame = null;
      apply(savedView(), false);
    });
  }
  function boot() {
    apply(savedView(), false);
    var host = document.getElementById('ProductGridContainer');
    if (host) new MutationObserver(queueApply).observe(host, { childList: true, subtree: true });
  }

  document.addEventListener('click', function (event) {
    /* The grid also stores its current state in data-maison-plp-view. Scope
       delegation to real controls or every product-card click is cancelled. */
    var button = event.target.closest('.maison-plp-view-button[data-maison-plp-view]');
    if (!button) return;
    event.preventDefault();
    animate(button.dataset.maisonPlpView, button);
  });
  document.addEventListener('maison:grid:append', queueApply);
  document.addEventListener('shopify:section:load', queueApply);
  if (desktopMQ.addEventListener) desktopMQ.addEventListener('change', queueApply);
  else desktopMQ.addListener(queueApply);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();


/* PDP accordion — "B · The draw" (design ACCORDION OPEN · PDP DETAILS, option B).
   Summary clicks are intercepted so <details>[open] can outlive the class-driven
   close transition (grid-rows → 0fr); [open] drops after the longest exit
   (.6s hairline). One open row per info column, matching the design demo.
   CSS owns all motion — this only moves classes. */
(function () {
  var HOLD = 650;
  function rows(scope) { return (scope || document).querySelectorAll('details.maison-pdp-accordion'); }
  function openRow(d) {
    clearTimeout(d.__maisonAccT);
    d.setAttribute('open', '');
    requestAnimationFrame(function () { requestAnimationFrame(function () { d.classList.add('maison-acc-open'); }); });
  }
  function closeRow(d) {
    clearTimeout(d.__maisonAccT);
    d.classList.remove('maison-acc-open');
    d.__maisonAccT = setTimeout(function () { d.removeAttribute('open'); }, HOLD);
  }
  function boot() {
    var list = rows();
    if (!list.length) return;
    var fresh = [];
    list.forEach(function (d) {
      if (d.classList.contains('maison-acc-js')) return;
      fresh.push(d);
      d.classList.add('maison-acc-noanim', 'maison-acc-js');
      if (d.hasAttribute('open')) d.classList.add('maison-acc-open');
    });
    if (!fresh.length) return;
    requestAnimationFrame(function () { requestAnimationFrame(function () {
      fresh.forEach(function (d) { d.classList.remove('maison-acc-noanim'); });
    }); });
  }
  document.addEventListener('click', function (e) {
    var s = e.target && e.target.closest ? e.target.closest('summary.maison-pdp-accordion__row') : null;
    if (!s) return;
    var d = s.closest('details.maison-pdp-accordion');
    if (!d || !d.classList.contains('maison-acc-js')) return;
    e.preventDefault();
    if (d.classList.contains('maison-acc-open')) { closeRow(d); return; }
    var scope = d.closest('.product__info-container') || d.parentElement;
    rows(scope).forEach(function (o) { if (o !== d && o.classList.contains('maison-acc-open')) closeRow(o); });
    openRow(d);
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
